import { useEffect, useMemo, useState } from "react";
import { Feature } from "geojson";
import { useSnackbar } from "notistack";

// MUI
import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
  Tooltip,
  Stack,
  TextField,
  Box,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// MUI Icons
import DownloadIcon from "@mui/icons-material/DownloadRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";

// Project import
import { useSelector } from "store";
import { selectUiBranding } from "store/reducers/branding";
import IconButton from "components/@extended/IconButton";
import { useTranslation } from "utils/locales/utilityFunctions";
import Selector from "components/select/selector";
import TextFieldInput from "components/input/textField";
import NonForestBiomassForm from "./nonForestBiomassForm";
import ForestBiomassForm from "./forestBiomassForm";
import { ActivityBiomassScreening, FirestoreActivityProps } from "types/firestoreFeatures";
import { BIOMASS_CATEGORIES } from "./options";
import { FirestoreFileProps } from "types/files";
import {
  mapFromFileEntryPropsToFileListItem,
  mapFromFileToFileListItem,
} from "features/files/utils/fileUtilities";
import ScreeningDocument from "./screeningDocument";
import dayjs from "dayjs";
import useSpeciesData from "hooks/useSpeciesData";
import useActivityData from "hooks/useActivityData";
import usePrintMap from "hooks/usePrintMap";
import useMapData from "hooks/useMapData";
import {
  bytesToPdfFile,
  downloadPdfBytes,
  mergePdfBuffers,
  renderReactPdfToArrayBufferLike,
} from "utils/pdfUtils";
import useAuth from "hooks/useAuth";
import FilesSection from "./filesSection";
import { THEME_GROUPS_DK } from "config/config-general";
import { urlToDataUrl } from "utils/fileUtils";
import { getInternalAppType } from "utils/brandingUtils";
import BiomassScreeningCoreFields from "./components/biomassScreeningCoreFields";
import BiomassScreeningCertificateFields from "./components/biomassScreeningCertificateFields";

type BiomassProjectFormProps = {
  loading: boolean;
  withDocking?: boolean;
  biomassScreeningData: ActivityBiomassScreening;
  inputDisabled: boolean;
  additionalFormData: {
    responsible: string;
    estate: string;
    company: string;
    projectTitle: string;
    taskTitle: string;
    taskID: string;
    note: string;
    species: string[] | undefined;
    estateType: "contact" | "string";
  };
  existingFiles: FirestoreFileProps[];
  existingActivity?: FirestoreActivityProps;
  derivedActivityData?: {
    totalArea: number;
    largestArea: number;
  };
  newFiles: { file: File; metaData?: any }[];
  setBiomassScreeningData: ({
    value,
    key,
    parent,
  }: {
    value: any;
    key: string;
    parent?: string;
  }) => void;
  setOpenFilePickerModal: (state: boolean, categories?: string[]) => void;
  onSaveFiles: (
    activityId: string,
    projectId: string,
    files: File[],
    meta?: any,
    correlationId?: string,
  ) => Promise<void>;
  onChangeFile: (file: FirestoreFileProps) => void;
  onDownloadFile: (fileUrl: string, fileName?: string) => void;
  onDeleteFile: (file: FirestoreFileProps) => void;
  //   onChangeNewFile: (file: File) => void;
  onDeleteNewFile: (index: number) => void;
  setParentDisableState: (state: boolean) => void;
};

const BiomassProjectForm = ({
  loading,
  withDocking,
  inputDisabled,
  biomassScreeningData,
  additionalFormData,
  existingFiles,
  newFiles,
  existingActivity,
  derivedActivityData,
  setBiomassScreeningData,
  setOpenFilePickerModal,
  onSaveFiles,
  onChangeFile,
  onDownloadFile,
  onDeleteFile,
  onDeleteNewFile,
  setParentDisableState,
}: BiomassProjectFormProps) => {
  // --- Hooks --- //
  const { translate } = useTranslation();
  const { setBackgroundPrintJobLoading, userOrg, user } = useAuth();
  const { activityFeatures } = useMapData();
  const { getSpeciesNameAndAbbreviation } = useSpeciesData();
  const { getActivityName } = useActivityData();
  const { printMap, cancelPrint, isPrinting } = usePrintMap();
  const { enqueueSnackbar } = useSnackbar();
  const ui = useSelector(selectUiBranding);

  // --- States --- //
  const [printLogoDataUrl, setPrintLogoDataUrl] = useState<string | undefined>(undefined);

  // --- Effects --- //
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const url = ui.assets?.printHeaderLogo?.url;
      if (!url) return setPrintLogoDataUrl(undefined);
      if (url.endsWith(".png")) {
        // If it's a PNG, we can use it directly
        return setPrintLogoDataUrl(url);
      }
      try {
        const dataUrl = await urlToDataUrl(url);
        if (!cancelled) setPrintLogoDataUrl(dataUrl);
      } catch {
        if (!cancelled) setPrintLogoDataUrl(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ui.assets?.printHeaderLogo?.url]);

  const existingFilesArray = useMemo(() => {
    return existingFiles.map((file) =>
      mapFromFileEntryPropsToFileListItem({
        file,
        config: {
          view: "enabled",
          upload: inputDisabled ? "disabled" : "enabled",
          change: inputDisabled ? "disabled" : "enabled",
          download: "enabled",
          delete: inputDisabled ? "disabled" : "enabled",
        },
        onChange: onChangeFile,
        onDownload: (file) => onDownloadFile(file.url, file.name),
        onDelete: onDeleteFile,
        translate: translate,
      }),
    );
  }, [existingFiles]);

  const newFilesArray = useMemo(() => {
    return newFiles.map((file, idx) =>
      mapFromFileToFileListItem({
        file: file.file,
        index: idx,
        config: {
          view: "enabled",
          upload: inputDisabled ? "disabled" : "enabled",
          change: inputDisabled ? "disabled" : "enabled",
          download: "enabled",
          delete: inputDisabled ? "disabled" : "enabled",
        },
        translate,
        // onChange: onChangeNewFile,
        // onDownload: (file) => onDownloadFile(file.url, file.name),
        onDelete: onDeleteNewFile,
      }),
    );
  }, [newFiles]);

  const filesArray = [...existingFilesArray, ...newFilesArray];
  // --- Options --- //
  const biomassCategories = useMemo(() => {
    return BIOMASS_CATEGORIES.map((category) => ({
      id: category,
      label: translate(`biomass-project-form-category-${category}`),
    }));
  }, [translate("render-change")]);

  const biomassTypes: Record<string, { id: string; label: string; tooltip: string }[]> =
    useMemo(() => {
      return {
        agriculture: [
          {
            id: "energyWood",
            label: translate("biomass-project-form-type-energyWood"),
            tooltip: translate("biomass-project-form-type-energyWood-tooltip"),
          },
          {
            id: "agricultureResidues",
            label: translate("biomass-project-form-type-agricultureResidues"),
            tooltip: translate("biomass-project-form-type-agricultureResidues-tooltip"),
          },
        ],
        forest: [
          {
            id: "roundwood",
            label: translate("biomass-project-form-type-roundwood"),
            tooltip: translate("biomass-project-form-type-roundwood-tooltip"),
          },
          {
            id: "clearcutEnergyWood",
            label: translate("biomass-project-form-type-clearcutEnergyWood"),
            tooltip: translate("biomass-project-form-type-clearcutEnergyWood-tooltip"),
          },
          {
            id: "residues",
            label: translate("biomass-project-form-type-residues"),
            tooltip: translate("biomass-project-form-type-residues-tooltip"),
          },
        ],
        industryWaste: [
          {
            id: "cleanWaste",
            label: translate("biomass-project-form-type-cleanWaste"),
            tooltip: translate("biomass-project-form-type-cleanWaste-tooltip"),
          },
        ],
        infrastructure: [
          {
            id: "infrastructure",
            label: translate("biomass-project-form-type-infrastructure"),
            tooltip: translate("biomass-project-form-type-infrastructure-tooltip"),
          },
        ],
        nonforest: [
          {
            id: "residuesNonforest",
            label: translate("biomass-project-form-type-residuesNonforest"),
            tooltip: translate("biomass-project-form-type-residuesNonforest-tooltip"),
          },
        ],
      };
    }, [translate("render-change")]);

  const riscAssessments: Record<
    string,
    { id: string; label: string; disabled?: boolean; tooltip?: string }[]
  > = useMemo(() => {
    // Check for disabled options based on selected category
    return {
      agriculture: [
        // REMOVED: SBP maybe implemented later
        // {
        //   id: "ortho2008",
        //   label: translate("biomass-project-form-supplementary-ortho2008"),
        //   disabled: biomassScreeningData?.type === "agricultureResidues",
        //   tooltip: translate("biomass-project-form-supplementary-ortho2008-tooltip"),
        // },
        // {
        //   id: "declarationPesticides",
        //   label: translate("biomass-project-form-supplementary-declarationPesticides"),
        //   disabled: biomassScreeningData?.type === "energyWood",
        //   tooltip: translate("biomass-project-form-supplementary-declarationPesticides-tooltip"),
        // },
      ],
      forest: [
        { id: "certified", label: translate("biomass-project-form-supplementary-certified") },
        {
          id: "notCertified",
          label: translate("biomass-project-form-supplementary-notCertified"),
        },
      ],
      industryWaste: [
        { id: "certified", label: translate("biomass-project-form-supplementary-certified") },
      ],
      infrastructure: [
        {
          id: "additionalInfo",
          label: translate("biomass-project-form-supplementary-additionalInfo"),
          tooltip: translate("biomass-project-form-supplementary-additionalInfo-tooltip"),
        },
      ],
      nonforest: [
        {
          id: "naturecareS3",
          label: translate("biomass-project-form-supplementary-naturecareS3"),
        },
        { id: "shelterbelt", label: translate("biomass-project-form-supplementary-shelterbelt") },
      ],
    };
  }, [translate("render-change"), biomassScreeningData]);

  // --- Dynamic states --- //
  const iaVersion = getInternalAppType({
    pathOrHost: window.location.host,
    orgData: userOrg,
    userData: user,
  });
  const showCertField =
    biomassScreeningData &&
    [BIOMASS_CATEGORIES[1], BIOMASS_CATEGORIES[2]].includes(biomassScreeningData.category) &&
    biomassScreeningData.riscAssessment.includes("certified");

  const shouldRenderForestForm =
    biomassScreeningData &&
    biomassScreeningData.category === BIOMASS_CATEGORIES[1] &&
    biomassScreeningData.type &&
    biomassScreeningData.riscAssessment &&
    biomassScreeningData.riscAssessment.includes("notCertified");

  const shouldRenderNonForestForm =
    (biomassScreeningData &&
      [BIOMASS_CATEGORIES[0]].includes(biomassScreeningData?.category) &&
      biomassScreeningData.type) ||
    (biomassScreeningData &&
      [BIOMASS_CATEGORIES[4]].includes(biomassScreeningData?.category) &&
      biomassScreeningData.type &&
      biomassScreeningData.riscAssessment.length > 0);
  // biomassScreeningData.type &&
  // biomassScreeningData.riscAssessment.length > 0 &&
  // !biomassScreeningData.riscAssessment.includes("declarationPesticides")) ||
  // (biomassScreeningData && biomassScreeningData.type === "agricultureResidues");

  let shouldRenderCascadeSection = false;
  if (biomassScreeningData) {
    if (biomassScreeningData.category === BIOMASS_CATEGORIES[2]) {
      shouldRenderCascadeSection = true;
    } else if (
      biomassScreeningData.category === BIOMASS_CATEGORIES[4] &&
      biomassScreeningData.type &&
      biomassScreeningData.riscAssessment.length > 0
    ) {
      shouldRenderCascadeSection = true;
    } else if (
      biomassScreeningData?.category === BIOMASS_CATEGORIES[1] &&
      biomassScreeningData.type &&
      biomassScreeningData.riscAssessment.length > 0
    ) {
      shouldRenderCascadeSection = true;
    }
  }

  const formatField = (value: any, translationPrefix?: string, withoutTranslation?: boolean) => {
    if (value === undefined || value === null || value.length === 0) return undefined;
    if (value === "") return "-";
    if (withoutTranslation) return value;
    // Check if value is an array and string the values together
    if (Array.isArray(value)) {
      if (value.length === 0) return "-";
      return translationPrefix
        ? value.map((el) => translate(`${translationPrefix}-${el}`)).join(", ")
        : value.map((el) => translate(`${el}`)).join(", ");
    }
    return translationPrefix ? translate(`${translationPrefix}-${value}`) : translate(`${value}`);
  };

  // Set document for download
  let filesStr = undefined;
  if (existingFiles.length > 0) {
    filesStr = existingFiles.map((f) => f.name).join(", ");
  }
  if (newFiles.length > 0) {
    filesStr = filesStr
      ? filesStr + ", " + newFiles.map((f) => f.file.name).join(", ")
      : newFiles.map((f) => f.file.name).join(", ");
  }

  const dataObjectScreening = {
    headerLabels: {
      title: translate("biomass-project-form-title"),
      date: translate("biomass-project-form-date"),
      estate:
        additionalFormData.estateType === "contact"
          ? translate("biomass-project-form-estate")
          : translate("activity-information-owner"),
      company: translate("biomass-project-form-company"),
      projectTitle: translate("print-project-name"),
      taskTitle: translate("print-activity-name"),
      // taskID: translate("biomass-project-form-task-id"),
      responsible: translate("biomass-project-form-responsible"),
      note: translate("biomass-project-form-note"),
    },
    headerData: {
      date: `${dayjs().format("DD.MM.YYYY")}`,
      estate: additionalFormData.estate || "-",
      company: additionalFormData.company || "-",
      projectTitle: additionalFormData.projectTitle || "-",
      taskTitle: additionalFormData.taskTitle || "-",
      // taskID: additionalFormData.taskID || "-",
      responsible: additionalFormData.responsible || "-",
      note: additionalFormData.note || "-",
    },

    speciesLabels: {
      header: translate("biomass-project-form-species"),
      species: translate("biomass-project-form-species"),
    },
    speciesData: {
      species:
        additionalFormData?.species === undefined
          ? undefined
          : !additionalFormData.species || additionalFormData.species.length === 0
          ? "-"
          : additionalFormData.species
              .map((val) => getSpeciesNameAndAbbreviation(val).name)
              .join(", "),
    },

    biomassLabels: {
      header: translate("biomass-project-form-biomass"),
      category: translate("biomass-project-form-category-label"),
      type: translate("biomass-project-form-type-label"),
      riscAssessment: translate("biomass-project-form-risk-label"),
      certificationNumbers: translate("biomass-project-form-certificate-number"),
    },
    biomassData: {
      category: formatField(biomassScreeningData?.category, "biomass-project-form-category"),
      type: formatField(biomassScreeningData?.type, "biomass-project-form-type"),
      riscAssessment:
        biomassScreeningData?.riscAssessment === null
          ? "-"
          : biomassScreeningData?.riscAssessment === undefined
          ? undefined
          : typeof biomassScreeningData.riscAssessment === "string"
          ? translate(`biomass-project-form-supplementary-${biomassScreeningData.riscAssessment}`)
          : biomassScreeningData.riscAssessment.length === 0
          ? "-"
          : biomassScreeningData.riscAssessment
              .map((val) => translate(`biomass-project-form-supplementary-${val}`))
              .join(", "),
      fscCertificationNumber: biomassScreeningData?.riscAssessment?.includes("certified")
        ? biomassScreeningData?.fscCertificationNumber || undefined
        : undefined,
      pefcCertificationNumber: biomassScreeningData?.riscAssessment?.includes("certified")
        ? biomassScreeningData?.pefcCertificationNumber || undefined
        : undefined,
      legacyCertificationNumber: biomassScreeningData?.riscAssessment?.includes("certified")
        ? biomassScreeningData?.certificationNumber || undefined
        : undefined,
    },

    statusLabels: {
      header: translate("biomass-project-form-status"),
      protectedForest: translate("biomass-project-form-protected-forest"),
      greenOperationsPlan: translate("biomass-project-form-green-plan"),
      constraintsMap: translate("biomass-project-form-protection-map"),
    },
    statusData: {
      protectedForest: formatField(biomassScreeningData?.status?.protectedForest),
      greenOperationsPlan: formatField(biomassScreeningData?.status?.greenOperationsPlan),
      constraintsMap: formatField(biomassScreeningData?.status?.constraintsMap),
    },

    standAndHarvestLabels: {
      header: translate("biomass-project-form-vegetation-and-intervention"),
      undergrowthInOldForest: translate("biomass-project-form-undergrowth-old-forest"),
      unevenAgedFoliage: translate("biomass-project-form-uneven-broadleaves"),
      activity: translate("biomass-project-form-harvest-category"),
      regeneration: translate("biomass-project-form-regeneration-agreement"),
      expertReview: translate("biomass-project-form-forestry-assessment"),
      appendix16Value: translate("biomass-project-form-appendix16-value"),
      appendix25Value: translate("biomass-project-form-appendix25-value"),
      forestryEvaluation: translate("biomass-project-form-forestry-assessment-title"),
      forestryEvaluationAssessment: translate("biomass-project-form-forestry-evaluation-assessment"),
    },
    standAndHarvestData: {
      undergrowthInOldForest: formatField(
        biomassScreeningData?.standAndActivity?.undergrowthInOldForest,
      ),
      unevenAgedFoliage: formatField(biomassScreeningData?.standAndActivity?.unevenAgedFoliage),
      activity: formatField(
        biomassScreeningData?.standAndActivity?.activity,
        "biomass-project-form-harvest",
      ),
      regeneration:
        biomassScreeningData?.standAndActivity?.regeneration === undefined
          ? undefined
          : biomassScreeningData?.standAndActivity?.regeneration
          ? translate("yes")
          : translate("no"),
      expertReview:
        biomassScreeningData?.standAndActivity?.expertReview === undefined
          ? undefined
          : biomassScreeningData?.standAndActivity?.expertReview
          ? translate("biomass-project-form-expert-review")
          : "-",
      appendix16Value:
        biomassScreeningData?.standAndActivity?.appendix16Value === null ||
        biomassScreeningData?.standAndActivity?.appendix16Value === ""
          ? "-"
          : biomassScreeningData?.standAndActivity?.appendix16Value ?? undefined,
      appendix25Value:
        biomassScreeningData?.standAndActivity?.appendix25Value === null ||
        biomassScreeningData?.standAndActivity?.appendix25Value === ""
          ? "-"
          : biomassScreeningData?.standAndActivity?.appendix25Value ?? undefined,
      forestryEvaluation:
        biomassScreeningData?.standAndActivity?.forestryEvaluation === undefined
          ? undefined
          : biomassScreeningData?.standAndActivity?.forestryEvaluation === null
          ? "-"
          : typeof biomassScreeningData.standAndActivity.forestryEvaluation === "string"
          ? biomassScreeningData.standAndActivity.forestryEvaluation
          : biomassScreeningData.standAndActivity.forestryEvaluation.length === 0
          ? "-"
          : biomassScreeningData.standAndActivity.forestryEvaluation
              .map((val) => translate(`biomass-project-form-forestry-${val}`))
              .join(", "),
      forestryEvaluationAssessment:
        biomassScreeningData?.standAndActivity?.forestryEvaluation?.includes("assessment")
          ? biomassScreeningData.standAndActivity.forestryEvaluationAssessment
            ? translate(
                `biomass-project-form-forestry-evaluation-assessment-option-${biomassScreeningData.standAndActivity.forestryEvaluationAssessment}`,
              )
            : "-"
          : undefined,
      category: biomassScreeningData?.category,
    },

    natureLabels: {
      header: translate("biomass-project-form-nature-and-protection"),
      hnvForestAbove9: translate("biomass-project-form-hnv-over-9"),
      hnvAbove5: translate("biomass-project-form-hnv-over-5"),
      hnvValueForest: translate("biomass-project-form-hnv-forest-value"),
      hnvValue: translate("biomass-project-form-hnv-value"),
      nbl: translate("biomass-project-form-nbl"),
      heathland: translate("biomass-project-form-heathland"),
      dispensation: translate("biomass-project-form-dispensation"),
      natura2000: translate("biomass-project-form-natura2000"),
      subjectToNotification: translate("biomass-project-form-subject-to-notification"),
      heritageSite: translate("biomass-project-form-heritage-site"),
      redlistSpecies: translate("biomass-project-form-redlist-species"),
      skl25: translate("biomass-project-form-skl25"),
      natureValues: translate("biomass-project-form-nature-values"),
    },
    natureData: {
      hnvForestAbove9: formatField(biomassScreeningData?.natureAndConservation?.hnvForestAbove9),
      hnvAbove5: formatField(biomassScreeningData?.natureAndConservation?.hnvAbove5),
      hnvValue: formatField(biomassScreeningData?.natureAndConservation?.hnvValue, undefined, true),
      nbl: formatField(biomassScreeningData?.natureAndConservation?.nbl),
      heathland: formatField(biomassScreeningData?.natureAndConservation?.heathland),
      dispensation: formatField(biomassScreeningData?.natureAndConservation?.dispensation),
      natura2000: formatField(biomassScreeningData?.natureAndConservation?.natura2000),
      subjectToNotification: formatField(
        biomassScreeningData?.natureAndConservation?.subjectToNotification,
      ),
      heritageSite: formatField(biomassScreeningData?.natureAndConservation?.heritageSite),
      redlistSpecies: formatField(biomassScreeningData?.natureAndConservation?.redlistSpecies),
      skl25: formatField(biomassScreeningData?.natureAndConservation?.skl25),
      natureValues: formatField(
        biomassScreeningData?.natureAndConservation?.natureValues,
        "biomass-project-form-nature-values",
      ),
    },

    specialRequirementsLabels: {
      header: translate("biomass-project-form-special-conditions"),
      customerReq: translate("biomass-project-form-customer-requirements"),
    },
    specialRequirementsData: {
      customerReq:
        biomassScreeningData?.customerRequirement === null ||
        biomassScreeningData?.customerRequirement === ""
          ? "-"
          : biomassScreeningData?.customerRequirement === undefined
          ? undefined
          : biomassScreeningData.customerRequirement,
    },

    filesLabels: {
      header: translate("biomass-project-form-files"),
      files: translate("biomass-project-form-files"),
    },
    filesData: {
      files: filesStr, //"Dokument.pdf, Dokument.pdf, ...",
    },

    cascadeLabels: {
      header: translate("biomass-project-form-cascade-requirement-title"),
      subHeader: translate("biomass-project-form-cascade-requirement-description"),
    },
    cascadeData: {
      jusitfication:
        biomassScreeningData?.cascadeRequirements?.justification === null ||
        biomassScreeningData?.cascadeRequirements?.justification === ""
          ? "-"
          : biomassScreeningData?.cascadeRequirements?.justification === undefined
          ? undefined
          : biomassScreeningData.cascadeRequirements?.justification,
    },

    screeningDateLabels: {
      header: translate("screening-date-title"),
      subHeader: translate("screening-date-description"),
    },
    screeningDateData: {
      date:
        biomassScreeningData?.screeningDate === null || biomassScreeningData?.screeningDate === ""
          ? "-"
          : biomassScreeningData?.screeningDate === undefined
          ? undefined
          : `${dayjs(biomassScreeningData.screeningDate).format("DD.MM.YYYY")}`,
    },

    screeningFooter: translate(`print-footer-${iaVersion}`),
  };

  const document = <ScreeningDocument printLogo={printLogoDataUrl} {...dataObjectScreening} />;

  // --- Functions --- //
  // Handle print
  const handlePrint = async () => {
    setParentDisableState(true);
    setBackgroundPrintJobLoading(true);
    enqueueSnackbar(translate("print-screening-and-map-in-progress"), {
      variant: "info",
    });
    let features: Feature[] = [];
    if (existingActivity && existingActivity.type && activityFeatures[existingActivity.id] ) {
      const activityType = getActivityName(existingActivity.type.id);
      features = Object.values(activityFeatures[existingActivity.id]).map((f) => {
        const newProps = { ...f.properties, activityType };
        f.properties = newProps;
        return f;
      });
    }
    // Get screening document as pdf
    const screeningDocBytes = await renderReactPdfToArrayBufferLike(document);
    // Get relevant overlays from config
    let primOverlays = THEME_GROUPS_DK[0].primaryLayers;
    let secOverlays = [THEME_GROUPS_DK[0].secondaryLayers[0]];
    if (biomassScreeningData?.category === "forest") {
      primOverlays = THEME_GROUPS_DK[1].primaryLayers;
      secOverlays = THEME_GROUPS_DK[1].secondaryLayers;
    }

    const res = await printMap({
      // debug: true,
      printMode: "triple",
      backgroundLayerId: "googleTerrain",
      overlayLayersIds: primOverlays,
      secondaryOverlayLayersIds: secOverlays,
      dataOptions: {
        transparent: true,
        secondaryTransparent: true,
      },
      printOptions: {
        quality: 2,
      },
      features: features,
    });
    let mergedBytes: any = screeningDocBytes;
    if (Array.isArray(res)) {
      mergedBytes = await mergePdfBuffers([screeningDocBytes, ...res]);
    } else if (typeof res === "string") {
      if (res === "print-map-no-bbox") {
        enqueueSnackbar(translate("print-activity-no-features"), {
          variant: "warning",
        });
      } else {
        // TODO: Not fail proof yet, can be other errors as well
        // Error in print map (probably being cancled)
        setParentDisableState(false);
        setBackgroundPrintJobLoading(false);
        return;
      }
    }
    const generatedAt = dayjs().format("YYYY-MM-DD_HH-mm-ss-SSS");
    const fileName =
      existingActivity && existingActivity.name
        ? `${existingActivity.name}-screening-${generatedAt}.pdf`
        : `screening-${generatedAt}.pdf`;

    // Save file to storage
    const file = bytesToPdfFile(mergedBytes, fileName);
    const projectId = existingActivity ? existingActivity.projectId : "";
    const activityId = existingActivity ? existingActivity.id : "";
    await onSaveFiles(activityId, projectId, [file], {
      categories: ["project", "activity", "screening", "completeDocument"],
    });
    // Trigger download
    downloadPdfBytes(mergedBytes, fileName);
    setParentDisableState(false);
    setBackgroundPrintJobLoading(false);
  };

  // --- Render --- //
  return (
    <Grid container spacing={{ xs: 2 }}>
      <BiomassScreeningCoreFields
        data={biomassScreeningData}
        setData={setBiomassScreeningData}
        inputDisabled={inputDisabled}
        isPrinting={isPrinting}
        loading={loading}
        showCertificateFields={!!showCertField}
        onAdditionalInfoUpload={(categories) => setOpenFilePickerModal(true, categories)}
      />

      <BiomassScreeningCertificateFields
        data={biomassScreeningData}
        setData={setBiomassScreeningData}
        disabled={inputDisabled || isPrinting}
      />

      {/* <Grid item xs={12}>
        <PDFViewer width="100%" height="1000">
          {document}
        </PDFViewer>
      </Grid> */}

      {shouldRenderForestForm && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 0 }} />
          </Grid>

          <ForestBiomassForm
            biomassScreeningData={biomassScreeningData}
            inputDisabled={inputDisabled || isPrinting}
            derivedActivityData={derivedActivityData}
            files={filesArray}
            setBiomassScreeningData={setBiomassScreeningData}
            setOpenFilePickerModal={setOpenFilePickerModal}
          />
        </>
      )}

      {shouldRenderNonForestForm && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 0 }} />
          </Grid>

          <NonForestBiomassForm
            biomassScreeningData={biomassScreeningData}
            inputDisabled={inputDisabled || isPrinting}
            files={filesArray}
            setBiomassScreeningData={setBiomassScreeningData}
            setOpenFilePickerModal={setOpenFilePickerModal}
          />
        </>
      )}

      {/* {!(filesArray.length > 0) && (
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>
      )} */}

      {/* Cacade section */}
      {shouldRenderCascadeSection && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" sx={{ my: -1 }}>
              {translate("biomass-project-form-cascade-requirement-title")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography color="secondary" sx={{ mb: 1 }}>
              {translate("biomass-project-form-cascade-requirement-description")}
            </Typography>

            <TextFieldInput
              title={translate("biomass-project-form-cascade-requirement")}
              value={biomassScreeningData?.cascadeRequirements?.justification || ""}
              showTitle={false}
              showLabel={true}
              disabled={inputDisabled || isPrinting}
              handleChange={(e) =>
                setBiomassScreeningData({
                  value: e.target.value,
                  parent: "cascadeRequirements",
                  key: "justification",
                })
              }
              fullWidth
            />
          </Grid>
        </>
      )}

      {/* Screening date picker */}
      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h5" sx={{ my: -1 }}>
          {translate("screening-date-title")}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography color="secondary" sx={{ mb: 1 }}>
          {translate("screening-date-description")}
        </Typography>

        <Grid container>
          <Grid item xs={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale("da")}>
              <DatePicker
                label={translate("screening-date")}
                disabled={inputDisabled || isPrinting}
                value={
                  biomassScreeningData?.screeningDate
                    ? dayjs(biomassScreeningData.screeningDate).toDate()
                    : null
                }
                onChange={(newValue) => {
                  setBiomassScreeningData({
                    value: dayjs(newValue).format(),
                    key: "screeningDate",
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    fullWidth
                    color="secondary"
                    {...params}
                    error={false}
                    sx={{
                      "& .MuiFormLabel-root": { fontSize: 14.5 },
                      "& label": {
                        marginTop: biomassScreeningData?.screeningDate ? "0px" : "-5px",
                        "&.Mui-focused": {
                          marginTop: "0px",
                        },
                        color: "secondary.600",
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Grid>

      {/* ------------------- Files ------------------- */}
      {filesArray.length > 0 && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <FilesSection files={filesArray} disabled={inputDisabled || isPrinting} />
        </>
      )}

      {/* {filesArray.length === 0 && <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
      </Grid>} */}

      {withDocking && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <Stack direction={"column"} spacing={1}>
                <Typography variant="h5">{translate("biomass-project-form-title")}</Typography>

                <Typography color="secondary" sx={{ mb: 1 }}>
                  {translate("biomass-project-form-title-download-req")}
                </Typography>
              </Stack>

              <Tooltip title={translate("biomass-project-form-download")}>
                <span>
                  <IconButton
                    variant="light"
                    color="secondary"
                    sx={{ bgcolor: "grey.100", ml: 1 }}
                    onClick={handlePrint}
                    disabled={loading || isPrinting || inputDisabled}
                    loading={isPrinting}
                  >
                    <DownloadIcon
                      fontSize="small"
                      color={loading || isPrinting || inputDisabled ? "disabled" : "primary"}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Grid>
        </>
      )}

      {!withDocking && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <Stack direction={"column"} spacing={1}>
                <Typography variant="h5">{translate("biomass-project-form-title")}</Typography>

                <Typography color="secondary" sx={{ mb: 1 }}>
                  {translate("biomass-project-form-title-download-req")}
                </Typography>
              </Stack>

              <Tooltip title={translate("biomass-project-form-download")}>
                <span>
                  <IconButton
                    variant="light"
                    color="secondary"
                    sx={{ bgcolor: "grey.100", ml: 1 }}
                    onClick={handlePrint}
                    disabled={loading || isPrinting || inputDisabled}
                    loading={isPrinting}
                  >
                    <DownloadIcon
                      fontSize="small"
                      color={loading || isPrinting || inputDisabled ? "disabled" : "primary"}
                    />
                  </IconButton>
                </span>
              </Tooltip>

              {isPrinting && (
                <Tooltip title={translate("biomass-project-form-download-cancel")}>
                  <span>
                    <IconButton
                      variant="light"
                      color="secondary"
                      sx={{ bgcolor: "grey.100" }}
                      onClick={cancelPrint}
                    >
                      <CloseIcon fontSize="small" color={"primary"} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default BiomassProjectForm;
